import { useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from "date-fns";
import TaskModal from "./TaskModal";
import { useTasks } from "../hooks/useTasks";
import { useAuth } from "../contexts/AuthContext";
import ProgressBar from "./ProgressBar";
import { useTasksForPeriod } from "../hooks/useTasks";

export default function MonthCalendar() {
  const today = new Date();
  const { isDemoMode, user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const goToToday = () => {
    setCurrentMonth(today);
  };

  const { tasks: monthTasks } = useTasksForPeriod(monthStart, monthEnd, isDemoMode, user?.uid);
  const completedCount = monthTasks.filter((t) => t.completed).length;
  const progress =
    monthTasks.length > 0
      ? Math.round((completedCount / monthTasks.length) * 100)
      : 0;

  const getTasksForDay = (day) => {
    return monthTasks.filter((task) => {
      const taskDate = task.date?.toDate
        ? task.date.toDate()
        : new Date(task.date);
      return isSameDay(taskDate, day);
    });
  };

  const handleDayClick = (day) => {
    setSelectedDate(day);
    setIsModalOpen(true);
  };

  const weekDays = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  return (
    <>
      <div className="bg-[#202020] rounded-3xl px-3 md:px-6 pt-3 md:pt-6 pb-3 shadow-[9px_9px_15px_rgba(0,0,0,0.4)]">
        <div className="bg-amber-600 rounded-t-3xl -mx-3 md:-mx-6 -mt-3 md:-mt-6 mb-3 md:mb-4 p-3 md:p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousMonth}
              className="text-white hover:bg-amber-700 rounded-full p-1.5 md:p-2 transition-colors"
              aria-label="Previous month"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex flex-col items-center">
              <h2 className="text-white text-base md:text-xl font-bold uppercase">
                {format(currentMonth, "MMMM yyyy")}
              </h2>
              {!isSameMonth(currentMonth, today) && (
                <button
                  onClick={goToToday}
                  className="text-white text-[10px] md:text-xs hover:underline mt-0.5 md:mt-1 opacity-80"
                >
                  Today
                </button>
              )}
            </div>
            
            <button
              onClick={goToNextMonth}
              className="text-white hover:bg-amber-700 rounded-full p-1.5 md:p-2 transition-colors"
              aria-label="Next month"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1.5 md:gap-2 mb-2 md:mb-4">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-[10px] md:text-xs font-bold text-gray-400 uppercase py-1 md:py-2"
            >
              {/* Mobile: First letter only, Desktop: First 3 letters */}
              <span className="md:hidden">{day.charAt(0)}</span>
              <span className="hidden md:inline">{day.substring(0, 3)}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5 md:gap-2">
          {days.map((day) => {
            const dayTasks = getTasksForDay(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isTodayDate = isSameDay(day, today);

            return (
              <div
                key={day.toISOString()}
                onClick={() => handleDayClick(day)}
                className={`aspect-square p-1 md:p-2 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors flex flex-col ${
                  isCurrentMonth ? "bg-[#303030]" : "bg-gray-800 opacity-50"
                } ${isTodayDate ? "ring-2 ring-amber-600" : ""}`}
              >
                <div
                  className={`text-[10px] md:text-sm font-bold mb-0.5 md:mb-1 flex-shrink-0 ${
                    isCurrentMonth ? "text-white" : "text-gray-500"
                  }`}
                >
                  {format(day, "d")}
                </div>
                <div className="space-y-0.5 md:space-y-1 flex-1 overflow-hidden">
                  {dayTasks.slice(0, 2).map((task) => (
                    <div
                      key={task.id}
                      className="text-[8px] md:text-xs bg-amber-600 text-white px-0.5 md:px-1 py-0.5 rounded md:rounded-md truncate leading-tight"
                    >
                      {task.text}
                    </div>
                  ))}
                  {dayTasks.length > 2 && (
                    <div className="text-[7px] md:text-xs text-gray-400 leading-tight">
                      +{dayTasks.length - 2}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <ProgressBar percentage={progress} label="Monthly progress:" />
      </div>

      {selectedDate && (
        <MonthDayModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
        />
      )}
    </>
  );
}

function MonthDayModal({ isOpen, onClose, selectedDate }) {
  const { isDemoMode, user } = useAuth();
  const { tasks, addTask, removeTask, toggleTask } = useTasks(selectedDate, isDemoMode, user?.uid);

  return (
    <TaskModal
      isOpen={isOpen}
      onClose={onClose}
      selectedDate={selectedDate}
      tasks={tasks}
      onAddTask={addTask}
      onRemoveTask={removeTask}
      onToggleTask={toggleTask}
    />
  );
}
